import { HeroBlock } from './HeroBlock';
import { TextBlock } from './TextBlock';
import { RichTextBlock } from './RichTextBlock';
import { ImageBlock } from './ImageBlock';
import { BannerBlock } from './BannerBlock';
import { CardGridBlock } from './CardGridBlock';
import { SpacerBlock } from './SpacerBlock';
import { HtmlBlock } from './HtmlBlock';
import { CollectionBlock } from './CollectionBlock';
import { LoyaltyPointsBlock } from './LoyaltyPointsBlock';
import { LoyaltyRewardsBlock } from './LoyaltyRewardsBlock';
import { LoyaltyTierBlock } from './LoyaltyTierBlock';
import { LoyaltyScoreWidget } from './LoyaltyScoreWidget';
import { LoyaltyLeaderboardWidget } from './LoyaltyLeaderboardWidget';
import { LoyaltyDashboardBlock } from './LoyaltyDashboardBlock';
import { LoyaltyBadgesBlock } from './LoyaltyBadgesBlock';
import { LoyaltyLeaderboardBlock } from './LoyaltyLeaderboardBlock';
import { LoyaltyProfileBlock } from './LoyaltyProfileBlock';
import { LockedContentBlock } from './LockedContentBlock';
import { CustomBlock } from './CustomBlock';
import { FeatureGridBlock } from './FeatureGridBlock';
import { TestimonialBlock } from './TestimonialBlock';
import { PricingTableBlock } from './PricingTableBlock';
import { FormBlock } from './FormBlock';
import { VideoBlock } from './VideoBlock';
import { AccordionBlock } from './AccordionBlock';
import { CountdownBlock } from './CountdownBlock';
import { RowBlock } from './RowBlock';

export interface CmsBlock {
  id: string;
  type: string;
  sortOrder: number;
  props: Record<string, unknown>;
  /** Set by the server when the block is gated for loyalty members only */
  loyaltyMembersOnly?: boolean;
  /** True when the block has been replaced with a locked teaser by the server */
  isLocked?: boolean;
  /** Child blocks for row/column containers (populated server-side) */
  children?: CmsBlock[];
}

export function BlockRenderer({ block }: { block: CmsBlock }) {
  const p = block.props;

  // Locked blocks are always rendered as the teaser component, regardless of
  // their original type (the server already replaced props with preview data).
  if (block.isLocked || block.type === 'locked_content') {
    return <LockedContentBlock props={p as Parameters<typeof LockedContentBlock>[0]['props']} />;
  }

  switch (block.type) {
    case 'hero':
      return <HeroBlock props={p as Parameters<typeof HeroBlock>[0]['props']} />;
    case 'text':
      return <TextBlock props={p as Parameters<typeof TextBlock>[0]['props']} />;
    case 'rich_text':
      return <RichTextBlock props={p as Parameters<typeof RichTextBlock>[0]['props']} />;
    case 'image':
      return <ImageBlock props={p as Parameters<typeof ImageBlock>[0]['props']} />;
    case 'banner':
      return <BannerBlock props={p as Parameters<typeof BannerBlock>[0]['props']} />;
    case 'card_grid':
      return <CardGridBlock props={p as Parameters<typeof CardGridBlock>[0]['props']} />;
    case 'spacer':
      return <SpacerBlock props={p as Parameters<typeof SpacerBlock>[0]['props']} />;
    case 'html':
      return <HtmlBlock props={p as Parameters<typeof HtmlBlock>[0]['props']} />;
    case 'collection':
      return <CollectionBlock props={p as Parameters<typeof CollectionBlock>[0]['props']} />;
    case 'loyalty_points':
      return <LoyaltyPointsBlock props={p as Parameters<typeof LoyaltyPointsBlock>[0]['props']} />;
    case 'loyalty_rewards':
      return (
        <LoyaltyRewardsBlock props={p as Parameters<typeof LoyaltyRewardsBlock>[0]['props']} />
      );
    case 'loyalty_tier':
      return <LoyaltyTierBlock props={p as Parameters<typeof LoyaltyTierBlock>[0]['props']} />;
    case 'loyalty_score_widget':
      return <LoyaltyScoreWidget props={p as Parameters<typeof LoyaltyScoreWidget>[0]['props']} />;
    case 'loyalty_leaderboard':
      return (
        <LoyaltyLeaderboardWidget
          props={p as Parameters<typeof LoyaltyLeaderboardWidget>[0]['props']}
        />
      );
    case 'block_type':
      return <CustomBlock props={p as Parameters<typeof CustomBlock>[0]['props']} />;
    case 'feature_grid':
      return <FeatureGridBlock props={p as Parameters<typeof FeatureGridBlock>[0]['props']} />;
    case 'testimonial':
      return <TestimonialBlock props={p as Parameters<typeof TestimonialBlock>[0]['props']} />;
    case 'pricing_table':
      return <PricingTableBlock props={p as Parameters<typeof PricingTableBlock>[0]['props']} />;
    case 'form':
      return <FormBlock props={p as Parameters<typeof FormBlock>[0]['props']} />;
    case 'video':
      return <VideoBlock props={p as Parameters<typeof VideoBlock>[0]['props']} />;
    case 'accordion':
      return <AccordionBlock props={p as Parameters<typeof AccordionBlock>[0]['props']} />;
    case 'countdown':
      return <CountdownBlock props={p as Parameters<typeof CountdownBlock>[0]['props']} />;
    case 'row':
      return <RowBlock block={block} />;
    // ── Loyalty full-page blocks (used inside a loyalty-sidebar CMS space) ──
    case 'loyalty_dashboard_page':
      return <LoyaltyDashboardBlock block={block} />;
    case 'loyalty_badges_page':
      return <LoyaltyBadgesBlock block={block} />;
    case 'loyalty_leaderboard_page':
      return <LoyaltyLeaderboardBlock block={block} />;
    case 'loyalty_profile_page':
      return <LoyaltyProfileBlock block={block} />;
    default:
      return null;
  }
}
